import classNames from 'classnames';
import {
    ButtonHTMLAttributes,
    Dispatch,
    HTMLAttributes,
    SetStateAction,
    createContext,
    forwardRef,
    useCallback,
    useContext,
    useEffect,
    useRef,
    useState,
} from 'react';
import { useIsomorphicLayoutEffect } from 'usehooks-ts';
import { useMounted } from '@/shared/hooks/use-mounted';
import { usePrevious } from '@/shared/hooks/use-previous';
import { getCSSCustomProp } from '@/shared/lib/utils/css';

interface Props extends HTMLAttributes<HTMLElement> {
    collapsed?: boolean;
    initialCollapsed?: boolean;
    onCollapse?: () => void;
    onExpand?: () => void;
    hover?: boolean;
}

const CollapseContext = createContext<{
    collapsed: boolean;
    setCollapsed: Dispatch<SetStateAction<boolean>>;
    hover: boolean;
}>({
    collapsed: true,
    setCollapsed: () => {},
    hover: false,
});

export const useCollapseContext = () => useContext(CollapseContext);

const Toggler = forwardRef<HTMLButtonElement, ButtonHTMLAttributes<HTMLButtonElement>>((props, ref) => {
    const { collapsed, setCollapsed, hover } = useCollapseContext();

    const handleMouseEnter = useCallback(() => {
        if (hover && collapsed) {
            setCollapsed(!collapsed);
        }
    }, [hover, collapsed, setCollapsed]);

    const handleMouseLeave = useCallback(() => {
        if (hover && !collapsed) {
            setCollapsed(!collapsed);
        }
    }, [hover, collapsed, setCollapsed]);

    const handleClick = useCallback(() => {
        setCollapsed(!collapsed);
    }, [collapsed, setCollapsed]);

    return (
        <button
            {...props}
            ref={ref}
            className={classNames('collapse__toggler', props.className)}
            onClick={(event) => {
                if (!hover) {
                    handleClick();
                }
                props.onClick?.(event);
            }}
            onMouseEnter={(event) => {
                if (hover) {
                    handleMouseEnter();
                }
                props.onMouseEnter?.(event);
            }}
            onMouseLeave={(event) => {
                if (hover) {
                    handleMouseLeave();
                }
                props.onMouseLeave?.(event);
            }}
            aria-expanded={!collapsed}
        ></button>
    );
});
Toggler.displayName = 'Toggler';

const Content = ({ children }: HTMLAttributes<HTMLElement>) => {
    const content = useRef<HTMLDivElement>(null);
    const { collapsed } = useCollapseContext();
    const [minVisibleHeight, setMinVisibleHeight] = useState(0);
    const [contentHeight, setContentHeight] = useState<number | string>(collapsed ? 0 : '');
    const isMounted = useMounted();

    const setUIState = useCallback(() => {
        if (content.current) {
            if (collapsed) {
                setContentHeight(Math.min(content.current.scrollHeight, minVisibleHeight));
            } else {
                setContentHeight(content.current.scrollHeight);
            }
        }
    }, [collapsed, minVisibleHeight]);

    useIsomorphicLayoutEffect(() => {
        setUIState();
    }, [setUIState]);

    useEffect(() => {
        let ro: ResizeObserver | null;

        if ('ResizeObserver' in window && content.current) {
            ro = new ResizeObserver((entries, observer) => {
                entries.forEach((entry) => {
                    observer.unobserve(entry.target);
                    setUIState();
                });
            });
            ro.observe(content.current);
        }

        return () => {
            if (ro) {
                ro.disconnect();
                ro = null;
            }
        };
    }, [setUIState]);

    useIsomorphicLayoutEffect(() => {
        const onResize = () => {
            if (content.current) {
                const parentCollapse = content.current.closest<HTMLElement>('.collapse');

                if (parentCollapse) {
                    const minHeight = getCSSCustomProp(parentCollapse, '--min-visible-height', 'number') as number;
                    setMinVisibleHeight(minHeight);
                }
            }
        };

        onResize();
        window.addEventListener('resize', onResize);

        return () => window.removeEventListener('resize', onResize);
    }, []);

    return (
        <div ref={content} className="collapse__content" style={{ height: isMounted ? contentHeight : undefined }}>
            {children}
        </div>
    );
};

const Collapse = ({
    children,
    initialCollapsed = true,
    collapsed,
    onCollapse,
    onExpand,
    hover = false,
    ...props
}: Props) => {
    const [innerCollapsed, setInnerCollapsed] = useState(typeof collapsed === 'boolean' ? collapsed : initialCollapsed);
    const prevInnerCollapsed = usePrevious(innerCollapsed);

    useEffect(() => {
        if (typeof collapsed !== 'undefined') {
            setInnerCollapsed(collapsed);
        }
    }, [collapsed]);

    useEffect(() => {
        if (typeof prevInnerCollapsed !== 'undefined') {
            if (innerCollapsed) {
                onCollapse?.();
            } else {
                onExpand?.();
            }
        }
    }, [prevInnerCollapsed, innerCollapsed, onCollapse, onExpand]);

    useEffect(() => {
        if (innerCollapsed && collapsed !== false) {
            setInnerCollapsed(true);
        }
    }, [collapsed, innerCollapsed]);

    return (
        <CollapseContext.Provider
            value={{
                collapsed: innerCollapsed,
                setCollapsed: setInnerCollapsed,
                hover,
            }}
        >
            <div
                {...props}
                className={classNames('collapse', props.className, { 'collapse--opened': !innerCollapsed })}
            >
                {children}
            </div>
        </CollapseContext.Provider>
    );
};

Collapse.Toggler = Toggler;
Collapse.Content = Content;

export default Collapse;
